import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import {
  DASHBOARD_NAV_ITEMS,
  DashboardNavGroup,
  DashboardNavItem,
  isNavGroup,
  pathMatches,
} from './dashboard-nav.config';
import { NavNeonIcon, neonMultiStyle, useQuickcamIconSet } from './NavNeonIcon';
import styles from './dashboard-nav.module.scss';

function useSidebarNavCollapsed(): boolean {
  const [collapsed, setCollapsed] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.dataset.quickcamSidebar === 'collapsed',
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () =>
      setCollapsed(root.dataset.quickcamSidebar === 'collapsed');
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-quickcam-sidebar'],
    });
    return () => observer.disconnect();
  }, []);

  return collapsed;
}

function groupCollapsedTarget(item: DashboardNavGroup): string {
  return item.navigateOnExpand || item.children[0]?.to || '/u/dashboard';
}

function withNavTooltip(
  key: string,
  label: string,
  collapsed: boolean,
  node: JSX.Element,
): JSX.Element {
  if (!collapsed) return node;
  return (
    <Tooltip key={key} title={label} placement="right" arrow describeChild>
      {node}
    </Tooltip>
  );
}

export type DashboardNavVariant = 'desktop' | 'mobile';

type DashboardNavListProps = {
  variant: DashboardNavVariant;
  isAdmin?: boolean;
  onNavigate?: () => void;
  onLogout?: (event: React.MouseEvent) => void;
  className?: string;
};

function DashboardNavList({
  variant,
  isAdmin = false,
  onNavigate,
  onLogout,
  className,
}: DashboardNavListProps): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;
  const iconSet = useQuickcamIconSet();
  const sidebarNavCollapsed = useSidebarNavCollapsed();
  const sidebarCollapsed = variant === 'desktop' && sidebarNavCollapsed;
  const [openGroupId, setOpenGroupId] = useState<string | null>(() => {
    const matched = DASHBOARD_NAV_ITEMS.find(
      item =>
        isNavGroup(item) &&
        (!item.adminOnly || isAdmin) &&
        pathMatches(pathname, item.match),
    );
    return matched && isNavGroup(matched) ? matched.id : null;
  });

  const { scrollItems, settingsItem } = useMemo(() => {
    const visible = DASHBOARD_NAV_ITEMS.filter(item => {
      if (item.adminOnly && !isAdmin) return false;
      if ('mobileOnly' in item && item.mobileOnly && variant !== 'mobile') {
        return false;
      }
      return true;
    });
    const settings = visible.find(item => item.id === 'settings') || null;
    const scroll = visible.filter(item => item.id !== 'settings');
    return { scrollItems: scroll, settingsItem: settings };
  }, [isAdmin, variant]);

  const toggleGroup = (item: DashboardNavItem) => {
    if (!isNavGroup(item)) return;
    const nextOpen = openGroupId === item.id ? null : item.id;
    setOpenGroupId(nextOpen);
    if (
      nextOpen &&
      item.navigateOnExpand &&
      !pathname.includes(item.match as string)
    ) {
      history.push(item.navigateOnExpand);
      onNavigate?.();
    }
  };

  const handleLeafClick = (
    event: React.MouseEvent,
    item: Extract<DashboardNavItem, { to: string }>,
  ) => {
    if (item.action === 'logout') {
      onLogout?.(event);
      onNavigate?.();
      return;
    }
    onNavigate?.();
  };

  const renderItem = (item: DashboardNavItem): JSX.Element | null => {
    if (isNavGroup(item)) {
      const open = openGroupId === item.id;
      const groupSelected = pathMatches(pathname, item.match);

      if (sidebarCollapsed) {
        const target = groupCollapsedTarget(item);
        return withNavTooltip(
          item.id,
          item.label,
          sidebarCollapsed,
          <ListItemButton
            className={styles.listItem}
            selected={groupSelected}
            component={Link}
            to={target}
            onClick={() => onNavigate?.()}
            data-nav-id={item.id}
            style={neonMultiStyle(item.id, iconSet)}
          >
            <ListItemIcon>
              <NavNeonIcon icon={item.icon} />
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>,
        );
      }

      return (
        <React.Fragment key={item.id}>
          <ListItemButton
            className={styles.listItem}
            selected={groupSelected}
            onClick={() => toggleGroup(item)}
            data-nav-id={item.id}
            style={neonMultiStyle(item.id, iconSet)}
          >
            <ListItemIcon>
              <NavNeonIcon icon={item.icon} />
            </ListItemIcon>
            <ListItemText primary={item.label} />
            <span className="material-icons">
              {open ? 'expand_less' : 'expand_more'}
            </span>
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List
              component="div"
              disablePadding
              className={styles.subMenu}
            >
              {item.children.map(child => (
                <ListItemButton
                  key={child.id}
                  className={styles.listItem}
                  selected={pathMatches(
                    pathname,
                    child.match,
                    child.exact,
                  )}
                  component={Link}
                  to={child.to}
                  onClick={event => handleLeafClick(event, child)}
                  data-nav-id={child.id}
                  style={neonMultiStyle(child.id, iconSet)}
                >
                  <ListItemIcon>
                    <NavNeonIcon icon={child.icon} />
                  </ListItemIcon>
                  <ListItemText primary={child.label} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    if (item.action === 'logout') {
      return (
        <ListItemButton
          key={item.id}
          className={styles.listItem}
          onClick={event => handleLeafClick(event, item)}
          data-nav-id={item.id}
          style={neonMultiStyle(item.id, iconSet)}
        >
          <ListItemIcon>
            <NavNeonIcon icon={item.icon} />
          </ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      );
    }

    const selected = pathMatches(pathname, item.match, item.exact);
    const linkTo =
      item.id === 'settings' && !pathname.includes('/settings')
        ? { pathname: item.to, state: { from: pathname } }
        : item.to;
    const leafButton = (
      <ListItemButton
        key={item.id}
        className={styles.listItem}
        selected={selected}
        component={Link}
        to={linkTo}
        onClick={event => handleLeafClick(event, item)}
        data-nav-id={item.id}
        style={neonMultiStyle(item.id, iconSet)}
      >
        <ListItemIcon>
          <NavNeonIcon icon={item.icon} />
        </ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    );
    return withNavTooltip(item.id, item.label, sidebarCollapsed, leafButton);
  };

  const settingsSelected =
    !!settingsItem &&
    !isNavGroup(settingsItem) &&
    pathMatches(pathname, settingsItem.match, settingsItem.exact);

  let settingsLinkTo: string | { pathname: string; state: { from: string } } = '/u/settings';
  if (settingsItem && !isNavGroup(settingsItem)) {
    if (!pathname.includes('/settings')) {
      settingsLinkTo = { pathname: settingsItem.to, state: { from: pathname } };
    } else {
      settingsLinkTo = settingsItem.to;
    }
  }

  const settingsButton =
    settingsItem && !isNavGroup(settingsItem) ? (
      <ListItemButton
        className={`${styles.listItem} ${styles.settingsAnchor}`}
        selected={settingsSelected}
        component={Link}
        to={settingsLinkTo}
        onClick={event => handleLeafClick(event, settingsItem)}
        data-nav-id={settingsItem.id}
        style={neonMultiStyle(settingsItem.id, iconSet)}
      >
        <ListItemIcon>
          <NavNeonIcon icon={settingsItem.icon} />
        </ListItemIcon>
        <ListItemText primary={settingsItem.label} />
      </ListItemButton>
    ) : null;

  return (
    <div className={[styles.navRoot, className].filter(Boolean).join(' ')}>
      <List
        component="nav"
        aria-label="Dashboard navigation"
        className={[
          styles.navList,
          variant === 'desktop' ? styles.navListDesktop : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {scrollItems.map(item => renderItem(item))}
      </List>
      {settingsButton && (
        <div className={styles.navFooter}>
          {withNavTooltip(
            'settings',
            settingsItem!.label,
            sidebarCollapsed,
            settingsButton,
          )}
        </div>
      )}
    </div>
  );
}

DashboardNavList.defaultProps = {
  isAdmin: false,
  onNavigate: undefined,
  onLogout: undefined,
  className: undefined,
};

export default DashboardNavList;
